import { createContext, useState, useContext, useEffect } from 'react';
import { fetchWrapperNoAuth, SERVER } from '../utils/helpers';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const USER_KEY = "IFydm";
  const PASS_KEY = "IFwyc";
  const AUTH_KEY = "IFawa";
  const IS_LOGGED_KEY = "IFIsAuth";
  const IS_SUBSCRIBED_KEY = "IFIsSubscribed";

  const IS_SUPER_USER_KEY = "IFIsSuperUser";
  const SUPER_USER_KEY = "IFSuperUser";
  const SUPER_USER_PASS_KEY = "IFSuperUserPass";
  const SUB_USERS_KEY = "IFSubUsers";
  const SUPER_USER_IS_SUBSCRIBED_KEY = "IFIsSubscribedSU";

  const [isAuth, setIsAuth] = useState(localStorage.getItem(IS_LOGGED_KEY));
  const [user, setUser] = useState(localStorage.getItem(USER_KEY));
  const [password, setPassword] = useState(localStorage.getItem(PASS_KEY));
  const [encodedAuth, setEncodedAuth] = useState(localStorage.getItem(AUTH_KEY));
  const [userData, setUserData] = useState(null);
  const [subscribed, setSubscribed] = useState(localStorage.getItem(IS_SUBSCRIBED_KEY));

  // Super user states
  const [isAuthSU, setIsAuthSU] = useState(localStorage.getItem(IS_SUPER_USER_KEY) === 'true');
  const [userSU, setUserSU] = useState(localStorage.getItem(SUPER_USER_KEY));
  const [passwordSU, setPassworsSU] = useState(localStorage.getItem(SUPER_USER_PASS_KEY));
  const [subUsers, setSubUsers] = useState(JSON.parse(localStorage.getItem(SUB_USERS_KEY) || '[]'));
  const [subscribedSU, setSubscribedSU] = useState(localStorage.getItem(SUPER_USER_IS_SUBSCRIBED_KEY));

  const isSubUser = userSU && user;

  // Regular user login
  const login = async (userResult) => {
    await checkSubscription(userResult.email.toLowerCase());
    let addresses = typeof userResult.address_list === 'string' ? JSON.parse(userResult.address_list) : userResult.address_list;
    const userDataTemp = {
      email: userResult.email.toLowerCase(),
      firstName: userResult.first_name || "",
      lastName: userResult.last_name || "",
      addresses: addresses,
      phone: userResult.phone || ""
    }
    setUserData(userDataTemp);
    if (!encodedAuth || (encodedAuth !== userResult.encoded_auth)) {
      saveKeys(userResult.encoded_auth || null);
    }

    setUser(userResult.email.toLowerCase());
    localStorage.setItem(USER_KEY, userResult.email.toLowerCase());
    setPassword(userResult.password);
    localStorage.setItem(PASS_KEY, userResult.password);

    setIsAuth(true);
    localStorage.setItem(IS_LOGGED_KEY, true);
  };

  // Super user login
  const loginSU = async (userResultSU) => {
    try {
      // Save super user credentials
      setUserSU(userResultSU.email.toLowerCase());
      localStorage.setItem(SUPER_USER_KEY, userResultSU.email.toLowerCase());
      setPassworsSU(userResultSU.password);
      localStorage.setItem(SUPER_USER_PASS_KEY, userResultSU.password);

      // Fetch sub-users if available
      if (userResultSU.sub_users) {
        setSubUsers(userResultSU.sub_users);
        localStorage.setItem(SUB_USERS_KEY, JSON.stringify(userResultSU.sub_users));
      } else {
        // If no sub-users in the result, fetch them separately
        await fetchSubUsers(userResultSU.email.toLowerCase(), userResultSU.password);
      }

      // Set auth states
      setIsAuth(true);
      localStorage.setItem(IS_LOGGED_KEY, true);
      setIsAuthSU(true);
      localStorage.setItem(IS_SUPER_USER_KEY, true);

      return true;
    } catch (error) {
      console.error("Super user login error:", error);
      return false;
    }
  };

  const fetchSubUsers = async (emailSU, passSU) => {
    try {
      const response = await fetchWrapperNoAuth(`${SERVER}/falcon_get_sub_users_SU`, emailSU.toLowerCase(), passSU);

      if (response && response.sub_users) {
        setSubUsers(response.sub_users);
        localStorage.setItem(SUB_USERS_KEY, response.sub_users);
      }
    } catch (error) {
      console.error("Error fetching sub-users:", error);
    }
  };

  const logout = () => {
    setUser(null);
    setPassword(null);
    setSubscribed(false);
    localStorage.removeItem(IS_SUBSCRIBED_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PASS_KEY);

    setIsAuth(false);
    localStorage.removeItem(IS_LOGGED_KEY);
  };

  const logoutSU = () => {
    setUserSU(null);
    setPassworsSU(null);
    setIsAuthSU(false);
    setSubUsers([]);
    localStorage.removeItem(IS_SUPER_USER_KEY);
    localStorage.removeItem(SUPER_USER_KEY);
    localStorage.removeItem(SUPER_USER_PASS_KEY);
    localStorage.removeItem(SUB_USERS_KEY);
  };

  const checkSubscription = async (email) => {
    if (email) {
      try {
        const result = await fetchWrapperNoAuth(`${SERVER}/falcon_get_sub_details/${email.toLowerCase()}/`, email, password);
        setSubscribed(result.active);
        localStorage.setItem(IS_SUBSCRIBED_KEY, result.active);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const checkSubscriptionSU = async (email) => {
    if (email) {
      try {
        const result = await fetchWrapperNoAuth(`${SERVER}/falcon_get_sub_details_su/${email.toLowerCase()}/`, email, passwordSU);
        setSubscribedSU(result.active);
        localStorage.setItem(SUPER_USER_IS_SUBSCRIBED_KEY, result.active);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const updateSubUser = async (subUserData) => {
    try {
      const body = {
        email: userSU,
        subUserData: subUserData
      }

      const response = await fetchWrapperNoAuth(`${SERVER}/falcon_update_sub_user`, userSU, passwordSU, { method: 'POST', body: body });

      if (response) {
        setSubUsers(response);
        localStorage.setItem(SUB_USERS_KEY, JSON.stringify(response));
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating sub-user:", error);
      return false;
    }
  };

  const removeSubUser = async (emailSU) => {
    try {
      const body = {
        email: userSU,
        emailSU: emailSU
      }

      const response = await fetchWrapperNoAuth(`${SERVER}/falcon_remove_sub_user`, userSU, passwordSU, { method: 'POST', body: body });

      if (response) {
        setSubUsers(response);
        localStorage.setItem(SUB_USERS_KEY, JSON.stringify(response));
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating sub-user:", error);
      return false;
    }
  };

  const saveKeys = (encodedAuthTemp) => {
    setEncodedAuth(encodedAuthTemp);
    localStorage.setItem(AUTH_KEY, encodedAuthTemp);
  };

  const getUserData = async () => {
    try {
      // Fetch user profile details
      const userResult = await fetchWrapperNoAuth(`${SERVER}/falcon_get_user/${user.toLowerCase()}`, user, password);
      let addresses = typeof userResult.address_list === 'string' ? JSON.parse(userResult.address_list) : userResult.address_list;
      const userDataTemp = {
        email: user.toLowerCase(),
        password: userResult.password,
        firstName: userResult.first_name || "",
        lastName: userResult.last_name || "",
        addresses: addresses,
        phone: userResult.phone || ""
      }

      setUser(userResult.email.toLowerCase());
      localStorage.setItem(USER_KEY, userResult.email.toLowerCase());
      setPassword(userResult.password);
      localStorage.setItem(PASS_KEY, userResult.password);
      setUserData(userDataTemp);
      if (!encodedAuth || (userResult.encoded_auth && userResult.encoded_auth !== encodedAuth)) {
        saveKeys(userResult.encoded_auth || null);
      }
      setIsAuth(true);

      const accessResponse = await fetchWrapperNoAuth(`${SERVER}/falcon_get_sub_details/${user.toLowerCase()}/`, user, password);
      setSubscribed(accessResponse.active);
      localStorage.setItem(IS_SUBSCRIBED_KEY, accessResponse.active);
    } catch (error) {
      console.error("User verification failed:", error);
      logout();
    }
  };

  const getSuperUserData = async () => {
    try {
      const userSUResult = await fetchWrapperNoAuth(`${SERVER}/falcon_get_user_su/${userSU.toLowerCase()}`, userSU, passwordSU);
      const subUsersTemp = typeof userSUResult.sub_users === 'string' ? JSON.parse(userSUResult.sub_users) : userSUResult.sub_users;
      setSubUsers(subUsersTemp);
      localStorage.setItem(SUB_USERS_KEY, JSON.stringify(subUsersTemp));
      setUserSU(userSUResult.email.toLowerCase());
      localStorage.setItem(SUPER_USER_KEY, userSUResult.email.toLowerCase())
      setPassworsSU(userSUResult.password);
      localStorage.setItem(SUPER_USER_PASS_KEY, userSUResult.password);
      setIsAuthSU(true);
      localStorage.setItem(IS_SUPER_USER_KEY, true);

      const accessResponse = await fetchWrapperNoAuth(`${SERVER}/falcon_get_sub_details_su/${userSU.toLowerCase()}/`, user, password);
      setSubscribedSU(accessResponse.active);
      localStorage.setItem(SUPER_USER_IS_SUBSCRIBED_KEY, accessResponse.active);
    } catch (error) {
      console.error("Super user verification failed:", error);
      logoutSU();
    }
  };

  const updateSubUserBillingRate = (userEmail, newRate) => {
    setSubUsers(prevSubUsers => {
      return prevSubUsers.map(user => {
        if (user.email === userEmail) {
          return {
            ...user,
            billingRate: newRate
          };
        }
        return user;
      });
    });
  };

  const updateSubUserItemQuantities = (userEmail, sku, field, newValue) => {
    setSubUsers(prevSubUsers => {
      return prevSubUsers.map(user => {
        if (user.email === userEmail) {
          // Find and update the specific item
          const updatedSentItems = (user.sent_items || []).map(item => {
            if (item.sku === sku) {
              return {
                ...item,
                [field]: newValue // This will be 'qty' or 'unpaidQty'
              };
            }
            return item;
          });

          return {
            ...user,
            sent_items: updatedSentItems
          };
        }
        return user;
      });
    });
  };

  const removeItemFromSubUser = (userEmail, removedItem) => {
    setSubUsers(prevSubUsers => {
      return prevSubUsers.map(user => {
        if (user.email === userEmail) {
          // Find and update the specific item
          const updatedSentItems = (user.sent_items || []).filter(item => item.sku !== removedItem.sku && item.date !== removedItem.date);

          return {
            ...user,
            sent_items: updatedSentItems
          };
        }
        return user;
      });
    });
  };

  useEffect(() => {
    const checkData = async () => {
      // Check SuperUser authentication
      if (userSU) {
        await getSuperUserData();
      } else {
        setIsAuthSU(false);
        localStorage.setItem(IS_SUPER_USER_KEY, false);

        // Check User authentication
        if (user) {
          await getUserData();
        } else {
          setIsAuth(false);
          localStorage.setItem(IS_LOGGED_KEY, false);
        }
      }
    }
    checkData();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuth,
        user,
        password,
        login,
        logout,
        subscribed,
        checkSubscription,
        encodedAuth,
        saveKeys,
        userData,
        setUserData,
        // Super user related
        isAuthSU,
        userSU,
        passwordSU,
        loginSU,
        logoutSU,
        subscribedSU,
        checkSubscriptionSU,
        subUsers,
        updateSubUser,
        removeSubUser,
        isSubUser,
        updateSubUserBillingRate,
        updateSubUserItemQuantities,
        removeItemFromSubUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}